/* eslint-disable store/split-to-modules */
import businessService from '@/modules/business/services/businessService';
import logError from '@/utils/logError/logError';
import webAIWidgetApiService from '../services/webAIWidgetApiService';
import webAIWidgetChatConfigService from '../services/webAIWidgetChatConfigService';
import { DEFAULT_CONFIG, DEFAULT_BUSINESS } from '../constants/webAIWidgetDefaults';
import formatTimeSlotsByDayToHumanReadable from '../utils/businessHoursFormatter';

const URL_KEYS = ['logo_url'];
const OMIT_KEYS = ['id', 'uid', 'business_id', 'created_at', 'updated_at'];

/**
 * Keys merged from the request payload into the committed config after save (API may omit or strip nulls).
 */
const CONFIG_PATCH_KEYS = [
  'widget_title',
  'button_text',
  'brand_color',
  'text_color',
  'logo_url',
  'welcome_message',
  'welcome_buttons',
  'actions',
  'auto_popup_enabled',
  'auto_popup_delay',
];

/**
 * Keys that belong to the AI chat configuration (webaiwidgetserver).
 * These are also saved to webaiwidgetmgr for backward compat during transition,
 * but the authoritative source is now webaiwidgetserver.
 */
const AI_CHAT_CONFIG_KEYS = [
  'faq_and_guidance',
  'business_hours',
  'lead_basic_contact_fields',
  'lead_fields',
  'include_business_hours',
  'include_contact_info',
  'include_services',
  'include_products',
  'include_booking_policies',
  'include_terms_of_service',
  'guidance_urls',
  'included_business_rule_categories',
];

const LEAD_FIELD_LABEL_MAX_LEN = 100;
const LEAD_FIELD_INSTRUCTIONS_MAX_LEN = 200;
const LEAD_FIELDS_MAX_ITEMS = 10;
const LEAD_BASIC_CONTACT_FIELDS_VALUES = ['phone_or_email', 'phone', 'email', 'both'];
const LEGACY_WIDGET_REPLACEMENT_TYPE = 'web_ai_widget';
const AI_CHAT_CONFIG_LOAD_ERROR_KEY = 'web_ai_widget.error_ai_config_load';
const AI_VOICE_EXTERNAL_APP_NAME = 'webaiwidgetserver';

function normalizeLeadBasicContactFields(value) {
  const v = String(value ?? '').trim();
  return LEAD_BASIC_CONTACT_FIELDS_VALUES.includes(v) ? v : null;
}

function normalizeLeadFields(value) {
  if (!Array.isArray(value)) return null;
  const list = value
    .slice(0, LEAD_FIELDS_MAX_ITEMS)
    .map((item) => {
      const label = String(item?.label ?? '').trim();
      const instructions = String(item?.instructions ?? '').trim();
      return {
        label: label ? label.slice(0, LEAD_FIELD_LABEL_MAX_LEN) : null,
        instructions: instructions ? instructions.slice(0, LEAD_FIELD_INSTRUCTIONS_MAX_LEN) : null,
      };
    })
    .filter((row) => row.label);
  return list.length > 0 ? list : [];
}

function buildWidgetActionsPayload(actions) {
  if (!Array.isArray(actions)) return actions ?? null;
  return actions.map((action, index) => ({
    uid: action?.uid ?? undefined,
    action: action?.action,
    availability: action?.availability !== false,
    label: action?.label ?? null,
    order: index,
  }));
}

function buildAiActionsPayload(actions) {
  if (!Array.isArray(actions)) return null;
  const list = actions
    .filter((action) => action?.uid || action?.action)
    .map((action) => ({
      action_uid: action?.uid ?? '',
      action_key: action?.action ?? '',
      should_offer: action?.should_offer === true,
      when_to_offer: action?.should_offer === true ? action?.when_to_offer ?? null : null,
    }));
  return list.length > 0 ? list : null;
}

function mergeAiActionsIntoWidgetActions(actions, aiActions) {
  if (!Array.isArray(actions) || !Array.isArray(aiActions) || aiActions.length === 0) {
    return actions;
  }
  const byUid = new Map();
  const byKey = new Map();
  aiActions.forEach((action) => {
    const uid = String(action?.action_uid ?? '').trim();
    const key = String(action?.action_key ?? '').trim().toLowerCase();
    if (uid) byUid.set(uid, action);
    if (key) byKey.set(key, action);
  });
  return actions.map((action) => {
    const uid = String(action?.uid ?? '').trim();
    const key = String(action?.action ?? '').trim().toLowerCase();
    const aiAction = (uid && byUid.get(uid)) || (key && byKey.get(key));
    if (!aiAction) return action;
    return {
      ...action,
      should_offer: aiAction.should_offer,
      when_to_offer: aiAction.when_to_offer ?? null,
    };
  });
}

function mergeAiChatConfigIntoWidgetConfig(config, aiChatConfig) {
  if (!config || !aiChatConfig) return config;
  const merged = { ...config };
  AI_CHAT_CONFIG_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(aiChatConfig, key)) {
      merged[key] = aiChatConfig[key];
    }
  });
  if (Object.prototype.hasOwnProperty.call(aiChatConfig, 'ai_actions')) {
    merged.actions = mergeAiActionsIntoWidgetActions(merged.actions, aiChatConfig.ai_actions);
  }
  return merged;
}

function buildAiChatPayload(config) {
  const payload = {};
  AI_CHAT_CONFIG_KEYS.forEach((key) => {
    if (config && Object.prototype.hasOwnProperty.call(config, key)) {
      payload[key] = config[key];
    }
  });
  if (Object.prototype.hasOwnProperty.call(payload, 'lead_basic_contact_fields')) {
    payload.lead_basic_contact_fields = normalizeLeadBasicContactFields(payload.lead_basic_contact_fields);
  }
  if (Object.prototype.hasOwnProperty.call(payload, 'lead_fields')) {
    payload.lead_fields = normalizeLeadFields(payload.lead_fields);
  }
  if (payload.guidance_urls === '') {
    payload.guidance_urls = null;
  }
  if (payload.guidance_urls != null && !Array.isArray(payload.guidance_urls)) {
    payload.guidance_urls = [payload.guidance_urls];
  }
  if (config && Object.prototype.hasOwnProperty.call(config, 'actions')) {
    payload.ai_actions = buildAiActionsPayload(config.actions);
  }
  return payload;
}

function parseLegacyWidgetReplacement(rawValue) {
  const raw = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (!raw) {
    return {
      enabled: false,
      malformed: false,
      raw: null,
      payload: null,
    };
  }

  try {
    const parsed = JSON.parse(raw);
    const isValid = parsed
      && typeof parsed === 'object'
      && parsed.type === LEGACY_WIDGET_REPLACEMENT_TYPE
      && typeof parsed.chat_service_url === 'string'
      && parsed.chat_service_url.trim() !== ''
      && typeof parsed.script_src === 'string'
      && parsed.script_src.trim() !== '';
    if (!isValid) {
      return {
        enabled: false,
        malformed: true,
        raw,
        payload: null,
      };
    }
    return {
      enabled: true,
      malformed: false,
      raw,
      payload: {
        type: parsed.type,
        chat_service_url: parsed.chat_service_url.trim(),
        script_src: parsed.script_src.trim(),
      },
    };
  } catch (_) {
    return {
      enabled: false,
      malformed: true,
      raw,
      payload: null,
    };
  }
}

/**
 * Build request payload for PUT main: snake_case, omit id/uid/business_id/dates,
 * empty URL fields as null. business_name can be overridden (e.g. from business object).
 * Action labels (*_text) are always included so reset-to-default persists (backend merge overwrites stored value).
 * @param {object} config - Full config (id, business_id, business_name, ...)
 * @param {string} [businessName] - Override for business_name
 * @returns {object} Payload for upsertMainConfig
 */
function buildRequestPayload(config, businessName) {
  if (!config) return {};
  const out = { ...config };
  OMIT_KEYS.forEach((k) => delete out[k]);
  AI_CHAT_CONFIG_KEYS.forEach((k) => delete out[k]);
  if (businessName !== undefined) {
    out.business_name = businessName;
  }
  URL_KEYS.forEach((k) => {
    if (out[k] === '' || out[k] === undefined) {
      out[k] = null;
    }
  });
  if (out.guidance_urls === '') {
    out.guidance_urls = null;
  }
  if (out.guidance_urls != null && !Array.isArray(out.guidance_urls)) {
    out.guidance_urls = [out.guidance_urls];
  }
  if (out.business_hours === '') {
    out.business_hours = null;
  }
  if (Object.prototype.hasOwnProperty.call(out, 'actions')) {
    out.actions = buildWidgetActionsPayload(out.actions);
  }
  if (out.welcome_message === '') {
    out.welcome_message = null;
  }
  if (out.button_text === '') {
    out.button_text = null;
  }
  if (out.widget_title === '') {
    out.widget_title = null;
  }
  if (out.faq_and_guidance === '') {
    out.faq_and_guidance = null;
  }
  if (out.brand_color === '') {
    out.brand_color = null;
  }
  if (out.text_color === '') {
    out.text_color = null;
  }
  return out;
}

const state = {
  config: null,
  /** AI chat configuration from webaiwidgetserver (knowledge, leads, AI actions). */
  aiChatConfig: null,
  aiChatConfigLoadError: null,
  business: null,
  rawBusinessName: null,
  /**
   * Brand color from GET platform/v1/businesses/:uid → business.meta.branding.color.
   * Same source as webaiwidgetmgr public config resolution when stored brand_color is null.
   */
  platformBrandColor: null,
  /**
   * Logo URL from GET platform/v1/businesses — meta.branding.logo_url, else large_logo_url.
   * Same resolution as webaiwidgetmgr public config when stored logo_url is null.
   */
  platformLogoUrl: null,
  /**
   * GET .../widget_configurations/public/:widget_uid — full resolved snapshot (same as embed).
   * Drives POV display defaults when stored fields are null/empty.
   */
  resolvedPublicConfig: null,
  isLoading: false,
  error: null,
  /** Available client portal actions for this business (from API or placeholder). Shape: [{ key, defaultLabel }]. */
  availablePortalActions: [],
  historyRefreshTrigger: 0,
  historyGoToEditorTrigger: 0,
  historyGoToChannelsTrigger: 0,
  editorSaveTrigger: 0,
  editorGoToHistoryTrigger: 0,
  editorShowEmbedTrigger: 0,
  editorOpenMoreMenuTrigger: 0,
  editorMoreSelectTrigger: { timestamp: 0, identifier: null },
  defaultBusinessHours: null,
  defaultBusinessHoursError: null,
  legacyWidgetReplacement: {
    enabled: false,
    malformed: false,
    raw: null,
    payload: null,
  },
  isLoadingLegacyWidgetReplacement: false,
  isSavingLegacyWidgetReplacement: false,
  voiceCallSettings: [],
  isLoadingVoiceCallSettings: false,
  isSavingVoiceCallSettings: false,
  voiceCallSettingsLoadError: null,
  voiceChannelSettingsByChannelUid: {},
  voiceChannelVoices: [],
  voiceChannelVoicesLoadError: null,
  isLoadingVoiceChannelVoices: false,
  isLoadingVoiceChannelSettings: false,
  isSavingVoiceChannelSettings: false,
};

const getters = {
  config: (s) => s.config,
  aiChatConfig: (s) => s.aiChatConfig,
  aiChatConfigLoadError: (s) => s.aiChatConfigLoadError,
  business: (s) => s.business,
  rawBusinessName: (s) => s.rawBusinessName,
  platformBrandColor: (s) => s.platformBrandColor,
  platformLogoUrl: (s) => s.platformLogoUrl,
  resolvedPublicConfig: (s) => s.resolvedPublicConfig,
  isLoading: (s) => s.isLoading,
  error: (s) => s.error,
  availablePortalActions: (s) => s.availablePortalActions,
  historyRefreshTrigger: (s) => s.historyRefreshTrigger,
  historyGoToEditorTrigger: (s) => s.historyGoToEditorTrigger,
  historyGoToChannelsTrigger: (s) => s.historyGoToChannelsTrigger,
  editorSaveTrigger: (s) => s.editorSaveTrigger,
  editorGoToHistoryTrigger: (s) => s.editorGoToHistoryTrigger,
  editorShowEmbedTrigger: (s) => s.editorShowEmbedTrigger,
  editorOpenMoreMenuTrigger: (s) => s.editorOpenMoreMenuTrigger,
  editorMoreSelectTrigger: (s) => s.editorMoreSelectTrigger,
  defaultBusinessHours: (s) => s.defaultBusinessHours,
  defaultBusinessHoursError: (s) => s.defaultBusinessHoursError,
  legacyWidgetReplacement: (s) => s.legacyWidgetReplacement,
  isLoadingLegacyWidgetReplacement: (s) => s.isLoadingLegacyWidgetReplacement,
  isSavingLegacyWidgetReplacement: (s) => s.isSavingLegacyWidgetReplacement,
  voiceCallSettings: (s) => s.voiceCallSettings,
  isLoadingVoiceCallSettings: (s) => s.isLoadingVoiceCallSettings,
  isSavingVoiceCallSettings: (s) => s.isSavingVoiceCallSettings,
  voiceCallSettingsLoadError: (s) => s.voiceCallSettingsLoadError,
  voiceChannelSettingsByChannelUid: (s) => s.voiceChannelSettingsByChannelUid,
  voiceChannelSettingsFor: (s) => (channelUid) => s.voiceChannelSettingsByChannelUid[channelUid] || null,
  voiceChannelVoices: (s) => s.voiceChannelVoices,
  voiceChannelVoicesLoadError: (s) => s.voiceChannelVoicesLoadError,
  isLoadingVoiceChannelVoices: (s) => s.isLoadingVoiceChannelVoices,
  isLoadingVoiceChannelSettings: (s) => s.isLoadingVoiceChannelSettings,
  isSavingVoiceChannelSettings: (s) => s.isSavingVoiceChannelSettings,
  isAiVoiceReceptionistEnabled: (s) => (
    s.voiceCallSettings.some((setting) => (
      setting?.external_app_name === AI_VOICE_EXTERNAL_APP_NAME
    ))
  ),
};

const mutations = {
  setConfig(state, config) {
    state.config = config;
  },
  setAiChatConfig(state, aiChatConfig) {
    state.aiChatConfig = aiChatConfig;
  },
  setAiChatConfigLoadError(state, error) {
    state.aiChatConfigLoadError = error || null;
  },
  setBusiness(state, business) {
    state.business = business;
  },
  setRawBusinessName(state, name) {
    state.rawBusinessName = name;
  },
  setPlatformBrandColor(state, color) {
    state.platformBrandColor = typeof color === 'string' && color.trim() !== '' ? color.trim() : null;
  },
  setPlatformLogoUrl(state, url) {
    state.platformLogoUrl = typeof url === 'string' && url.trim() !== '' ? url.trim() : null;
  },
  setResolvedPublicConfig(state, payload) {
    state.resolvedPublicConfig = payload && typeof payload === 'object' ? payload : null;
  },
  setLoading(state, value) {
    state.isLoading = value;
  },
  setError(state, error) {
    state.error = error;
  },
  setAvailablePortalActions(state, portalActions) {
    state.availablePortalActions = Array.isArray(portalActions) ? portalActions : [];
  },
  historyRefreshTrigger(state) {
    state.historyRefreshTrigger = Date.now();
  },
  historyGoToEditorTrigger(state) {
    state.historyGoToEditorTrigger = Date.now();
  },
  historyGoToChannelsTrigger(state) {
    state.historyGoToChannelsTrigger = Date.now();
  },
  editorSaveTrigger(state) {
    state.editorSaveTrigger = Date.now();
  },
  editorGoToHistoryTrigger(state) {
    state.editorGoToHistoryTrigger = Date.now();
  },
  editorShowEmbedTrigger(state) {
    state.editorShowEmbedTrigger = Date.now();
  },
  editorOpenMoreMenuTrigger(state) {
    state.editorOpenMoreMenuTrigger = Date.now();
  },
  setEditorMoreSelect(state, identifier) {
    state.editorMoreSelectTrigger = { timestamp: Date.now(), identifier };
  },
  setDefaultBusinessHours(state, value) {
    state.defaultBusinessHours = value;
    state.defaultBusinessHoursError = null;
  },
  setDefaultBusinessHoursError(state, message) {
    state.defaultBusinessHours = null;
    state.defaultBusinessHoursError = message;
  },
  setLegacyWidgetReplacement(state, value) {
    state.legacyWidgetReplacement = value || {
      enabled: false,
      malformed: false,
      raw: null,
      payload: null,
    };
  },
  setLoadingLegacyWidgetReplacement(state, value) {
    state.isLoadingLegacyWidgetReplacement = Boolean(value);
  },
  setSavingLegacyWidgetReplacement(state, value) {
    state.isSavingLegacyWidgetReplacement = Boolean(value);
  },
  setVoiceCallSettings(state, value) {
    state.voiceCallSettings = Array.isArray(value) ? value : [];
  },
  setVoiceCallSettingsLoadError(state, value) {
    state.voiceCallSettingsLoadError = value || null;
  },
  updateVoiceCallSetting(state, value) {
    if (!value?.uid) return;
    const index = state.voiceCallSettings.findIndex((setting) => setting?.uid === value.uid);
    if (index === -1) {
      state.voiceCallSettings = [...state.voiceCallSettings, value];
      return;
    }
    state.voiceCallSettings = state.voiceCallSettings.map((setting, settingIndex) => (
      settingIndex === index ? value : setting
    ));
  },
  setLoadingVoiceCallSettings(state, value) {
    state.isLoadingVoiceCallSettings = Boolean(value);
  },
  setSavingVoiceCallSettings(state, value) {
    state.isSavingVoiceCallSettings = Boolean(value);
  },
  setVoiceChannelSetting(state, value) {
    if (!value?.channel_uid) return;
    state.voiceChannelSettingsByChannelUid = {
      ...state.voiceChannelSettingsByChannelUid,
      [value.channel_uid]: value,
    };
  },
  removeVoiceChannelSetting(state, channelUid) {
    const next = { ...state.voiceChannelSettingsByChannelUid };
    delete next[channelUid];
    state.voiceChannelSettingsByChannelUid = next;
  },
  setVoiceChannelVoices(state, value) {
    state.voiceChannelVoices = Array.isArray(value) ? value : [];
  },
  setVoiceChannelVoicesLoadError(state, value) {
    state.voiceChannelVoicesLoadError = value || null;
  },
  setLoadingVoiceChannelVoices(state, value) {
    state.isLoadingVoiceChannelVoices = Boolean(value);
  },
  setLoadingVoiceChannelSettings(state, value) {
    state.isLoadingVoiceChannelSettings = Boolean(value);
  },
  setSavingVoiceChannelSettings(state, value) {
    state.isSavingVoiceChannelSettings = Boolean(value);
  },
};

const actions = {
  async fetchResolvedPublicConfig({ commit, state }, widgetUidOverride) {
    const widgetUid = widgetUidOverride ?? state.config?.id;
    if (!widgetUid) {
      commit('setResolvedPublicConfig', null);
      return;
    }
    try {
      const resolved = await webAIWidgetApiService.getPublicResolvedConfig(widgetUid);
      commit('setResolvedPublicConfig', resolved);
    } catch (err) {
      logError('Failed to fetch resolved public widget config', err, { widgetUid });
      commit('setResolvedPublicConfig', null);
    }
  },

  async fetchAvailablePortalActions({ commit, rootGetters }) {
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) return;
    try {
      const list = await webAIWidgetApiService.getAvailablePortalActions(businessUid);
      commit('setAvailablePortalActions', list);
    } catch (_) {
      commit('setAvailablePortalActions', []);
    }
  },

  async fetchDefaultBusinessHours({ commit, rootGetters }) {
    try {
      const staffOwner = rootGetters['StaffStore/staffOwner'];
      const uid = staffOwner?.weekly_availability_uid
        && String(staffOwner.weekly_availability_uid).trim();
      if (!uid) {
        commit('setDefaultBusinessHours', null);
        return;
      }
      const data = await webAIWidgetApiService.getWeeklyAvailabilityGeneral(uid);
      const slots = data?.time_slots_by_day;
      if (!Array.isArray(slots) || slots.length === 0) {
        commit('setDefaultBusinessHours', null);
        return;
      }
      const formatted = formatTimeSlotsByDayToHumanReadable(slots);
      const timeZone = typeof staffOwner?.time_zone_h === 'string'
        ? staffOwner.time_zone_h.trim()
        : '';
      // Display format for the preview panel — not a user-facing i18n string.
      const value = timeZone ? `${formatted}\n\nTime zone: ${timeZone}` : formatted;
      commit('setDefaultBusinessHours', value);
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 403
        ? 'web_ai_widget.content_tab.business_hours_unable_to_retrieve'
        : err?.message || null;
      commit('setDefaultBusinessHoursError', message);
    }
  },

  async fetchDefaults({ commit, state, rootGetters, dispatch }, options = {}) {
    const shouldCreateWidgetConfig = options?.createWidgetConfigIfMissing !== false;
    commit('setLoading', true);
    commit('setError', null);
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      if (!businessUid) {
        commit('setError', 'Business context required');
        return;
      }
      await dispatch('StaffStore/fetchAllStaff', null, { root: true });
      await dispatch('fetchDefaultBusinessHours');
      await webAIWidgetApiService.getAvailablePortalActions(businessUid).then((list) => {
        commit('setAvailablePortalActions', list);
      }).catch(() => commit('setAvailablePortalActions', []));
      await dispatch('fetchAiChatConfig');
      const result = await webAIWidgetApiService.getMainConfig(businessUid);
      let rawName = null;
      try {
        const data = await businessService.fetchBusiness(businessUid);
        rawName = data?.business?.business?.name || null;
        const branding = data?.business?.meta?.branding;
        const platformColor = branding?.color;
        commit(
          'setPlatformBrandColor',
          typeof platformColor === 'string' ? platformColor : null,
        );
        const logoPrimitive = typeof branding?.logo_url === 'string' && branding.logo_url.trim() !== ''
          ? branding.logo_url.trim()
          : null;
        const largeFallback = typeof branding?.large_logo_url === 'string' && branding.large_logo_url.trim() !== ''
          ? branding.large_logo_url.trim()
          : null;
        const platformLogo = logoPrimitive || largeFallback;
        commit('setPlatformLogoUrl', platformLogo);
      } catch (err) {
        logError('Failed to fetch business name for widget defaults', err);
        commit('setPlatformBrandColor', null);
        commit('setPlatformLogoUrl', null);
      }
      if (result.config !== null) {
        const loadedConfig = mergeAiChatConfigIntoWidgetConfig(
          { ...result.config },
          state.aiChatConfig,
        );
        await dispatch('fetchResolvedPublicConfig', loadedConfig.id);
        commit('setConfig', loadedConfig);
        commit('setBusiness', result.business);
        if (rawName) commit('setRawBusinessName', rawName);
        return;
      }
      const businessName = rawName || result.business?.name || DEFAULT_BUSINESS.name;
      commit('setRawBusinessName', businessName);
      const defaultConfig = {
        ...DEFAULT_CONFIG,
        business_id: businessUid,
        business_name: businessName,
        business_hours: null,
      };
      if (!shouldCreateWidgetConfig) {
        const loadedConfig = mergeAiChatConfigIntoWidgetConfig(defaultConfig, state.aiChatConfig);
        commit('setConfig', loadedConfig);
        commit('setBusiness', { id: businessUid, name: businessName });
        return;
      }
      const brandColor = state.platformBrandColor;
      if (brandColor == null || String(brandColor).trim() === '') {
        commit('setError', 'web_ai_widget.error_business_color_unavailable');
        return;
      }
      // Persist via PUT .../main so the widget uid exists before the editor preview loads (embed/public config).
      await dispatch('saveConfig', defaultConfig);
    } catch (err) {
      const status = err?.response?.status;
      const message = status === 403 ? 'web_ai_widget.error_forbidden' : err?.message || 'Failed to load';
      commit('setError', message);
    } finally {
      commit('setLoading', false);
    }
  },

  triggerHistoryRefresh({ commit }) {
    commit('historyRefreshTrigger');
  },
  triggerHistoryGoToEditor({ commit }) {
    commit('historyGoToEditorTrigger');
  },
  triggerHistoryGoToChannels({ commit }) {
    commit('historyGoToChannelsTrigger');
  },
  triggerEditorSave({ commit }) {
    commit('editorSaveTrigger');
  },
  triggerEditorGoToHistory({ commit }) {
    commit('editorGoToHistoryTrigger');
  },
  triggerEditorShowEmbed({ commit }) {
    commit('editorShowEmbedTrigger');
  },
  triggerEditorOpenMoreMenu({ commit }) {
    commit('editorOpenMoreMenuTrigger');
  },
  triggerEditorMoreSelect({ commit }, identifier) {
    commit('setEditorMoreSelect', identifier);
  },
  async saveConfig({ commit, state, rootGetters, dispatch }, payload) {
    commit('setError', null);
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) {
      const err = new Error('Business context required');
      throw err;
    }
    const configSource = payload ?? state.config;
    // Preserve null when user chose "default" (don't replace with business name)
    const businessName = configSource && Object.prototype.hasOwnProperty.call(configSource, 'business_name')
      ? configSource.business_name
      : state.business?.name;
    const requestPayload = buildRequestPayload(configSource, businessName);
    const result = await webAIWidgetApiService.upsertMainConfig(
      businessUid,
      requestPayload,
    );
    let configToCommit = { ...result.config };
    CONFIG_PATCH_KEYS.forEach((key) => {
      if (requestPayload[key] !== undefined) {
        configToCommit[key] = requestPayload[key];
      }
    });
    configToCommit = mergeAiChatConfigIntoWidgetConfig(configToCommit, state.aiChatConfig);
    await dispatch('fetchResolvedPublicConfig', configToCommit.id);
    commit('setConfig', configToCommit);
    commit('setBusiness', result.business);
    return configToCommit;
  },

  async fetchAiChatConfig({ commit }) {
    commit('setAiChatConfigLoadError', null);
    try {
      const data = await webAIWidgetChatConfigService.getMainConfig();
      commit('setAiChatConfig', data?.config ?? null);
    } catch (err) {
      logError('Failed to fetch AI chat config', err);
      commit('setAiChatConfig', null);
      commit('setAiChatConfigLoadError', AI_CHAT_CONFIG_LOAD_ERROR_KEY);
    }
  },

  /**
   * Save AI chat configuration fields to webaiwidgetserver.
   * Accepts a partial config object — only provided keys are sent.
   * @param {object} payload - Partial AI chat config (faq_and_guidance, lead_basic_contact_fields, lead_fields, etc.)
   */
  async saveAiChatConfig({ commit, state }, payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (state.aiChatConfigLoadError) {
      throw new Error(state.aiChatConfigLoadError);
    }
    const requestPayload = buildAiChatPayload(payload);
    const result = await webAIWidgetChatConfigService.upsertMainConfig(requestPayload);
    const nextAiChatConfig = result?.config ?? null;
    commit('setAiChatConfig', nextAiChatConfig);
    commit('setAiChatConfigLoadError', null);
    if (state.config) {
      commit('setConfig', mergeAiChatConfigIntoWidgetConfig(state.config, nextAiChatConfig));
    }
    return result;
  },

  /**
   * Save AI action behavior (should_offer, when_to_offer) to webaiwidgetserver.
   * @param {Array} aiActions - Array of { action_uid, action_key, should_offer, when_to_offer }
   */
  async saveAiActions({ commit, state }, aiActions) {
    if (!Array.isArray(aiActions)) return null;
    if (state.aiChatConfigLoadError) {
      throw new Error(state.aiChatConfigLoadError);
    }
    const result = await webAIWidgetChatConfigService.upsertMainConfig({
      ai_actions: buildAiActionsPayload(aiActions),
    });
    commit('setAiChatConfig', result?.config ?? null);
    commit('setAiChatConfigLoadError', null);
    return result;
  },

  async saveBusinessName({ commit, state, rootGetters, dispatch }, payload) {
    commit('setError', null);
    const businessUid = rootGetters['AuthStore/businessUid'];
    if (!businessUid) {
      const err = new Error('Business context required');
      throw err;
    }
    const businessName = payload?.name ?? state.business?.name;
    const requestPayload = buildRequestPayload(
      state.config,
      businessName,
    );
    const result = await webAIWidgetApiService.upsertMainConfig(
      businessUid,
      requestPayload,
    );
    let configToCommit = { ...result.config };
    CONFIG_PATCH_KEYS.forEach((key) => {
      if (requestPayload[key] !== undefined) {
        configToCommit[key] = requestPayload[key];
      }
    });
    configToCommit = mergeAiChatConfigIntoWidgetConfig(configToCommit, state.aiChatConfig);
    await dispatch('fetchResolvedPublicConfig', configToCommit.id);
    commit('setConfig', configToCommit);
    commit('setBusiness', result.business);
    return result.business;
  },
  async fetchLegacyWidgetReplacement({ commit }) {
    commit('setLoadingLegacyWidgetReplacement', true);
    try {
      const res = await webAIWidgetApiService.getLegacyActiveEngageSettings();
      const parsed = parseLegacyWidgetReplacement(res?.sub_widget_type);
      commit('setLegacyWidgetReplacement', parsed);
      return parsed;
    } finally {
      commit('setLoadingLegacyWidgetReplacement', false);
    }
  },
  async fetchVoiceCallSettings({ commit }) {
    commit('setLoadingVoiceCallSettings', true);
    try {
      const settings = await webAIWidgetApiService.getVoiceCallSettings();
      commit('setVoiceCallSettings', settings);
      commit('setVoiceCallSettingsLoadError', null);
      return settings;
    } catch (err) {
      logError('Failed to fetch AI voice receptionist settings', err);
      commit('setVoiceCallSettings', []);
      commit('setVoiceCallSettingsLoadError', err);
      return [];
    } finally {
      commit('setLoadingVoiceCallSettings', false);
    }
  },
  async saveAiVoiceReceptionistEnabled({ commit }, { settingsUid, enabled }) {
    if (!settingsUid) {
      throw new Error('Voice call settings are not available');
    }
    commit('setSavingVoiceCallSettings', true);
    try {
      const settings = await webAIWidgetApiService.updateVoiceCallSettings(settingsUid, {
        external_app_name: enabled ? AI_VOICE_EXTERNAL_APP_NAME : null,
      });
      commit('updateVoiceCallSetting', settings);
      return settings;
    } finally {
      commit('setSavingVoiceCallSettings', false);
    }
  },
  async fetchVoiceChannelSettings({ commit }, { channelUid }) {
    if (!channelUid) {
      throw new Error('Voice channel is not available');
    }
    commit('setLoadingVoiceChannelSettings', true);
    try {
      const settings = await webAIWidgetApiService.getVoiceChannelSettings(channelUid);
      commit('setVoiceChannelSetting', settings);
      return settings;
    } finally {
      commit('setLoadingVoiceChannelSettings', false);
    }
  },
  async fetchVoiceChannelVoices({ commit, state }) {
    if (state.voiceChannelVoices.length > 0) {
      return state.voiceChannelVoices;
    }
    commit('setLoadingVoiceChannelVoices', true);
    try {
      const voices = await webAIWidgetApiService.getVoiceChannelVoices();
      commit('setVoiceChannelVoices', voices);
      commit('setVoiceChannelVoicesLoadError', null);
      return voices;
    } catch (err) {
      logError('Failed to fetch AI receptionist voices', err);
      commit('setVoiceChannelVoices', []);
      commit('setVoiceChannelVoicesLoadError', err);
      return [];
    } finally {
      commit('setLoadingVoiceChannelVoices', false);
    }
  },
  async previewVoiceChannelWelcomeMessage(_, { settings }) {
    try {
      return await webAIWidgetApiService.previewVoiceChannelWelcomeMessage(settings);
    } catch (err) {
      logError('Failed to generate AI receptionist voice preview', err);
      throw err;
    }
  },
  async saveVoiceChannelSettings({ commit, state }, { channelUid, settings }) {
    if (!channelUid) {
      throw new Error('Voice channel is not available');
    }
    commit('setSavingVoiceChannelSettings', true);
    try {
      const existing = state.voiceChannelSettingsByChannelUid[channelUid];
      const saved = existing?.uid
        ? await webAIWidgetApiService.updateVoiceChannelSettings(channelUid, settings)
        : await webAIWidgetApiService.createVoiceChannelSettings(channelUid, settings);
      commit('setVoiceChannelSetting', saved);
      return saved;
    } catch (error) {
      if (error?.response?.status === 409) {
        const saved = await webAIWidgetApiService.updateVoiceChannelSettings(channelUid, settings);
        commit('setVoiceChannelSetting', saved);
        return saved;
      }
      throw error;
    } finally {
      commit('setSavingVoiceChannelSettings', false);
    }
  },
  async deleteVoiceChannelSettings({ commit }, { channelUid }) {
    if (!channelUid) return;
    await webAIWidgetApiService.deleteVoiceChannelSettings(channelUid);
    commit('removeVoiceChannelSetting', channelUid);
  },
  async saveLegacyWidgetReplacement(
    { commit },
    { enabled, chatServiceUrl, scriptSrc },
  ) {
    commit('setSavingLegacyWidgetReplacement', true);
    try {
      if (!enabled) {
        await webAIWidgetApiService.updateLegacyActiveEngageSettings(null);
      } else {
        const payload = {
          type: LEGACY_WIDGET_REPLACEMENT_TYPE,
          chat_service_url: String(chatServiceUrl || '').trim(),
          script_src: String(scriptSrc || '').trim(),
        };
        await webAIWidgetApiService.updateLegacyActiveEngageSettings(JSON.stringify(payload));
      }
      const refreshed = await webAIWidgetApiService.getLegacyActiveEngageSettings();
      const parsed = parseLegacyWidgetReplacement(refreshed?.sub_widget_type);
      if (Boolean(parsed.enabled) !== Boolean(enabled)) {
        throw new Error('Legacy widget replacement setting mismatch after save');
      }
      commit('setLegacyWidgetReplacement', parsed);
      return parsed;
    } finally {
      commit('setSavingLegacyWidgetReplacement', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
